import PropTypes from "prop-types";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Sector
} from "recharts";
import { useState } from "react";
import "./common.css";

const COLORS = [
  "#F3AED3",  // Charts/Bar colors/Mid/7 — Plan Variation
  "#B6F7FF",  // Highlight/aqua/Aqua Pale
  "#60A5FA",  // color/brand/accent-1/low — Others
  "#8652DF",  // Colors/Lilac/Lilac — PR to PO Conversion
  "#E7DCF9",  // Colors/Lilac/Lilac pale
  "#23E7FF",  // Secondary accents/Water — Supplier & Transit Delay
  "#CFBAF2",  // Charts/Bar colors/Mid/6 — PR to PO Conversion
  "#0066CC",  // Colors/Sky/Sky
  "#47A3FF",  // Colors/Sky/Sky Mid — Supplier & Transit Delay
  "#C2E0FF",  // Colors/Sky/Sky pale — Others
];

let prevY = null;

const renderLabel = ({
  cx,
  cy,
  midAngle,
  outerRadius,
  percent,
  value
}) => {
  const RADIAN = Math.PI / 180;

  const baseOffset = 10;
  const smallSliceExtra = percent < 0.07 ? 8 : 0;

  const radius = outerRadius + baseOffset + smallSliceExtra;

  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  let y = cy + radius * Math.sin(-midAngle * RADIAN);

  const minGap = 16;

  if (prevY !== null && Math.abs(y - prevY) < minGap) {
    if (y > prevY) {
      y = prevY + minGap;
    } else {
      y = prevY - minGap;
    }
  }

  prevY = y;

  return (
    <text
      x={x}
      y={y}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      fontSize={13}
      fontWeight={600}
      fill="#111827"
    >
      {`${value} (${(percent * 100).toFixed(0)}%)`}
    </text>
  );
};

const renderActiveShape = (props) => {
  const {
    cx,
    cy,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill
  } = props;

  return (
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius + 8}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
    />
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    return (
      <div className="custom-tooltip">
        <div className="tooltip-row">
          <div className="tooltip-left">
            <span className="tooltip-label">{payload[0].name}</span>
          </div>
          <span className="tooltip-value">{payload[0].value}</span>
        </div>
      </div>
    );
  }
  return null;
};

const CommonSemiDonutChart = ({ data }) => {
  const [activeIndex, setActiveIndex] = useState(null);

  return (
    <div className="semi-donut-wrapper">
      <ResponsiveContainer width="100%" height="100%">

        <PieChart>
          <Tooltip content={<CustomTooltip />} />

          <Pie
            data={data}
            dataKey="value"
            startAngle={180}
            endAngle={0}
            cx="50%"
            cy="75%"
            innerRadius={100}
            outerRadius={160}
            paddingAngle={0}
            labelLine={false}
            label={renderLabel}
            activeIndex={activeIndex}
            activeShape={renderActiveShape}
            onMouseEnter={(_, index) => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(null)}
            isAnimationActive={false}
          >
            {data.map((entry, index) => (
              <Cell
                key={entry.name}
                fill={COLORS[index % COLORS.length]}
                cursor="pointer"
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>

      <div className="custom-legend">
        {data.map((item, index) => (
          <div className="legend-item" key={item.name}>
            <span
              className="legend-dot"
              style={{
                backgroundColor:
                  COLORS[index % COLORS.length]
              }}
            />
            {item.name}
          </div>
        ))}
      </div>
    </div>
  );
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ),
};

CommonSemiDonutChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ).isRequired,
};

export default CommonSemiDonutChart;
